import { Color, colorSetVariants } from '@/theme/partials/palette.css';
import { assertUnreachable } from '@liquorice/utils';

export type ColorScheme =
  | {
      __typename: 'colorScheme';
      label?: string | null;
    }
  | null
  | undefined;

export type TextColorScheme =
  | {
      __typename: 'textColorScheme';
      label?: string | null;
    }
  | null
  | undefined;

export const parseColorScheme = (data: Maybe<ColorScheme | TextColorScheme>): Color | undefined => {
  const label = data?.label;

  return label && label in colorSetVariants ? (label as Color) : undefined;
};

export const useColorScheme = (
  maybeColorScheme?: Maybe<ColorScheme>,
  customDefault?: Color
): { colorScheme: Color; oppositeScheme: Color } => {
  const parsedColor = parseColorScheme(maybeColorScheme);
  const defaultScheme: Color = customDefault ?? 'white';

  return {
    colorScheme: parsedColor ?? defaultScheme,
    oppositeScheme: findOppositeColorScheme(parsedColor ?? defaultScheme),
  };
};

/**
 * A switch statement that returns the opposite color scheme of the given color.
 * This is primarily used for text color.
 */
export const findOppositeColorScheme = (color: Color): Color => {
  switch (color) {
    case 'pink':
      return 'shiraz';
    case 'shiraz':
      return 'pink';
    case 'orange':
      return 'velvet';
    case 'navy':
      return 'blue';
    case 'eucalypt':
      return 'velvet';
    case 'brown':
      return 'green';
    case 'blue':
      return 'navy';
    case 'velvet':
      return 'eucalypt';
    case 'green':
      return 'brown';
    case 'black':
      return 'cream';
    case 'white':
      return 'black';
    case 'cream':
      return 'black';
    case 'error':
      return 'black';
    case 'success':
      return 'black';
    // Default case to ensure all colors are handled, even past the current set.
    default:
      return 'white';
  }
};
